import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import {
  NewbornPostpartumEncounterQuestionnaireURLs,
  NewbornPostpartumMedicationsTherapiesQuestionsLinkIds,
} from "@/encounters/constants/newborn-postpartum";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import { getFormsetAnswers } from "@/utils/questionnaires";

const MedicationsAndTherapiesDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          NewbornPostpartumEncounterQuestionnaireURLs.MedicationsTherapies,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  // If no medications and therapies data has ever been recorded
  if (!currentVersion) {
    return (
      <Paper p="md" withBorder>
        <Stack mb="lg">
          <Title order={2}>Meds & Therapies</Title>
        </Stack>
        <TextWrapper py="lg" c="gray.6" ta="center">
          No medications and therapies data has been recorded for this encounter.
        </TextWrapper>
      </Paper>
    );
  }

  // If medications and therapies data exists but no previous version to compare against,
  // create an empty previous version to reuse the existing diff logic
  const effectivePreviousVersion =
    previousVersion || ({ item: [] } as unknown as QuestionnaireResponse);

  // Extract formset data for both versions
  const previousMedicationsFormset = getFormsetAnswers(
    effectivePreviousVersion,
    NewbornPostpartumMedicationsTherapiesQuestionsLinkIds.MedicationFormset,
    NewbornPostpartumMedicationsTherapiesQuestionsLinkIds.MedicationFormsetCode,
  );
  const currentMedicationsFormset = getFormsetAnswers(
    currentVersion,
    NewbornPostpartumMedicationsTherapiesQuestionsLinkIds.MedicationFormset,
    NewbornPostpartumMedicationsTherapiesQuestionsLinkIds.MedicationFormsetCode,
  );

  const previousTherapiesFormset = getFormsetAnswers(
    effectivePreviousVersion,
    NewbornPostpartumMedicationsTherapiesQuestionsLinkIds.TherapiesFormset,
    NewbornPostpartumMedicationsTherapiesQuestionsLinkIds.TherapiesFormsetCode,
  );
  const currentTherapiesFormset = getFormsetAnswers(
    currentVersion,
    NewbornPostpartumMedicationsTherapiesQuestionsLinkIds.TherapiesFormset,
    NewbornPostpartumMedicationsTherapiesQuestionsLinkIds.TherapiesFormsetCode,
  );

  // Extract general notes
  const previousGeneralNotes =
    effectivePreviousVersion.item?.find(
      (item) => item.linkId === NewbornPostpartumMedicationsTherapiesQuestionsLinkIds.GeneralNotes,
    )?.answer?.[0]?.valueString || "";

  const currentGeneralNotes =
    currentVersion.item?.find(
      (item) => item.linkId === NewbornPostpartumMedicationsTherapiesQuestionsLinkIds.GeneralNotes,
    )?.answer?.[0]?.valueString || "";

  // Helper function to extract medication data as individual items
  const extractMedicationData = (medicationsFormset: Record<string, Record<string, unknown>[]>) => {
    const medications = Object.values(medicationsFormset);
    return medications
      .map((item) => {
        const medication = item[0];
        if (!medication) return null;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const med = medication as any;
        return {
          title: med.newbornPostpartumMedicationFormsetTitle?.valueString || "",
          dose:
            med.newbornPostpartumMedicationFormsetDoseAndRoute
              ?.newbornPostpartumMedicationFormsetDose?.valueString || "",
          route:
            med.newbornPostpartumMedicationFormsetDoseAndRoute
              ?.newbornPostpartumMedicationFormsetRoute?.valueCoding?.display || "",
          lotNumber: med.newbornPostpartumMedicationFormsetLotNumber?.valueString || "",
          expirationDate: med.newbornPostpartumMedicationFormsetExpirationDate?.valueDate || "",
          notes: med.newbornPostpartumMedicationFormsetNotes?.valueString || "",
        };
      })
      .filter((med): med is NonNullable<typeof med> => med !== null);
  };

  // Helper function to extract therapy data as individual items
  const extractTherapyData = (therapiesFormset: Record<string, Record<string, unknown>[]>) => {
    const therapies = Object.values(therapiesFormset);
    return therapies
      .map((item) => {
        const therapy = item[0];
        if (!therapy) return null;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ther = therapy as any;
        return {
          name: ther.newbornPostpartumTherapiesFormsetName?.valueString || "",
          notes: ther.newbornPostpartumTherapiesFormsetNotes?.valueString || "",
        };
      })
      .filter((therapy): therapy is NonNullable<typeof therapy> => therapy !== null);
  };

  const previousMedications = extractMedicationData(previousMedicationsFormset);
  const currentMedications = extractMedicationData(currentMedicationsFormset);
  const previousTherapies = extractTherapyData(previousTherapiesFormset);
  const currentTherapies = extractTherapyData(currentTherapiesFormset);

  // Check if both versions are completely empty
  const isCompletelyEmpty =
    !previousMedications.length &&
    !previousTherapies.length &&
    !previousGeneralNotes &&
    !currentMedications.length &&
    !currentTherapies.length &&
    !currentGeneralNotes;

  if (isCompletelyEmpty) {
    return (
      <Paper p="md" withBorder>
        <Stack mb="lg">
          <Title order={2}>Meds & Therapies</Title>
        </Stack>
        <TextWrapper py="lg" c="gray.6" ta="center">
          No medications and therapies data has been recorded for this encounter.
        </TextWrapper>
      </Paper>
    );
  }

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Meds & Therapies</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack>
              {previousMedications.length > 0 && (
                <Stack>
                  <TextWrapper fw={600}>Medications</TextWrapper>
                  {previousMedications.map((medication, index) => {
                    const currentMed = currentMedications[index];
                    return (
                      <Stack p="sm" key={`prev-med-${index}`}>
                        {medication && (
                          <Stack gap="0">
                            <TextWrapper fw={600}>Medication {index + 1}</TextWrapper>
                            <WordsDiff
                              showRemoved
                              previous={medication.title}
                              current={currentMed?.title || ""}
                            />
                          </Stack>
                        )}
                        {medication.dose && (
                          <Stack gap="0">
                            <TextWrapper fw={600}>Dose</TextWrapper>
                            <WordsDiff
                              showRemoved
                              previous={medication.dose}
                              current={currentMed?.dose || ""}
                            />
                          </Stack>
                        )}
                        {medication.route && (
                          <Stack gap="0">
                            <TextWrapper fw={600}>Route</TextWrapper>
                            <WordsDiff
                              showRemoved
                              previous={medication.route}
                              current={currentMed?.route || ""}
                            />
                          </Stack>
                        )}
                        {medication.lotNumber && (
                          <Stack gap="0">
                            <TextWrapper fw={600}>Lot Number</TextWrapper>
                            <WordsDiff
                              showRemoved
                              previous={medication.lotNumber}
                              current={currentMed?.lotNumber || ""}
                            />
                          </Stack>
                        )}
                        {medication.expirationDate && (
                          <Stack gap="0">
                            <TextWrapper fw={600}>Expiration Date</TextWrapper>
                            <WordsDiff
                              showRemoved
                              previous={medication.expirationDate}
                              current={currentMed?.expirationDate || ""}
                            />
                          </Stack>
                        )}
                        {medication.notes && (
                          <Stack gap="0">
                            <TextWrapper fw={600}>Notes</TextWrapper>
                            <WordsDiff
                              showRemoved
                              previous={medication.notes}
                              current={currentMed?.notes || ""}
                            />
                          </Stack>
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              )}
              {previousTherapies.length > 0 && (
                <Stack gap="md">
                  <TextWrapper fw={600}>Therapies</TextWrapper>
                  {previousTherapies.map((therapy, index) => {
                    const currentTherapy = currentTherapies[index];
                    return (
                      <Stack p="sm" key={`prev-therapy-${index}`}>
                        {therapy && (
                          <Stack gap="0">
                            <TextWrapper fw={600}>Therapy {index + 1}</TextWrapper>
                            <WordsDiff
                              showRemoved
                              previous={therapy.name}
                              current={currentTherapy?.name || ""}
                            />
                          </Stack>
                        )}
                        {therapy.notes && (
                          <Stack gap="0">
                            <TextWrapper fw={600}>Notes</TextWrapper>
                            <WordsDiff
                              showRemoved
                              previous={therapy.notes}
                              current={currentTherapy?.notes || ""}
                            />
                          </Stack>
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              )}
              {previousGeneralNotes && (
                <Stack gap="0">
                  <TextWrapper fw={600}>Meds & Therapies Notes</TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentGeneralNotes}
                    previous={previousGeneralNotes}
                  />
                </Stack>
              )}
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              {currentMedications.length > 0 && (
                <Stack gap="md">
                  <TextWrapper fw={600}>Medications</TextWrapper>
                  {currentMedications.map((medication, index) => {
                    const previousMed = previousMedications[index];
                    return (
                      <Stack p="sm" key={`curr-med-${index}`}>
                        {medication && (
                          <Stack gap="0">
                            <TextWrapper fw={600}>Medication {index + 1}</TextWrapper>
                            <WordsDiff
                              current={medication.title}
                              previous={previousMed?.title || ""}
                            />
                          </Stack>
                        )}
                        {medication.dose && (
                          <Stack gap="0">
                            <TextWrapper fw={600}>Dose</TextWrapper>
                            <WordsDiff
                              current={medication.dose}
                              previous={previousMed?.dose || ""}
                            />
                          </Stack>
                        )}
                        {medication.route && (
                          <Stack gap="0">
                            <TextWrapper fw={600}>Route</TextWrapper>
                            <WordsDiff
                              current={medication.route}
                              previous={previousMed?.route || ""}
                            />
                          </Stack>
                        )}
                        {medication.lotNumber && (
                          <Stack gap="0">
                            <TextWrapper fw={600}>Lot Number</TextWrapper>
                            <WordsDiff
                              current={medication.lotNumber}
                              previous={previousMed?.lotNumber || ""}
                            />
                          </Stack>
                        )}
                        {medication.expirationDate && (
                          <Stack gap="0">
                            <TextWrapper fw={600}>Expiration Date</TextWrapper>
                            <WordsDiff
                              current={medication.expirationDate}
                              previous={previousMed?.expirationDate || ""}
                            />
                          </Stack>
                        )}
                        {medication.notes && (
                          <Stack gap="0">
                            <TextWrapper fw={600}>Notes</TextWrapper>
                            <WordsDiff
                              current={medication.notes}
                              previous={previousMed?.notes || ""}
                            />
                          </Stack>
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              )}
              {currentTherapies.length > 0 && (
                <Stack gap="md">
                  <TextWrapper fw={600}>Therapies</TextWrapper>
                  {currentTherapies.map((therapy, index) => {
                    const previousTherapy = previousTherapies[index];
                    return (
                      <Stack p="sm" key={`curr-therapy-${index}`}>
                        {therapy && (
                          <Stack gap="0">
                            <TextWrapper fw={600}>Therapy {index + 1}</TextWrapper>
                            <WordsDiff
                              current={therapy.name}
                              previous={previousTherapy?.name || ""}
                            />
                          </Stack>
                        )}
                        {therapy.notes && (
                          <Stack gap="0">
                            <TextWrapper fw={600}>Notes</TextWrapper>
                            <WordsDiff
                              current={therapy.notes}
                              previous={previousTherapy?.notes || ""}
                            />
                          </Stack>
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              )}
              {currentGeneralNotes && (
                <Stack gap="0">
                  <TextWrapper fw={600}>Meds & Therapies Notes</TextWrapper>
                  <WordsDiff current={currentGeneralNotes} previous={previousGeneralNotes} />
                </Stack>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default MedicationsAndTherapiesDiff;
